import React, { useId } from 'react';
import { PiCheckBold } from 'react-icons/pi';
import { ColorInput } from 'src/modules/shared/components/atoms/inputs/color-input/color-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  isSameWebsiteHexColor,
  isWebsiteHexColor,
  resolveWebsiteBrandTextTone,
  WEBSITE_BRAND_COLOR_SWATCHES,
  WEBSITE_BRAND_DARK_TEXT_COLOR,
  type WebsiteBrandTextTone,
} from '../../utils/website-brand-color.util';

const resolveColorHintKey = (
  isValidColor: boolean,
  textTone: WebsiteBrandTextTone
): TranslationKey => {
  if (!isValidColor) {
    return 'websites.settings.brand.color.invalid';
  }

  return textTone === 'light'
    ? 'websites.settings.brand.color.contrastLight'
    : 'websites.settings.brand.color.contrastDark';
};

/** Props for the brand color control. */
interface WebsiteBrandColorInputProps {
  /** Visible label of the control. */
  label: string;
  /** Current brand color, possibly incomplete while the user types. */
  value: string;
  /** Whether the current user may change the color. */
  disabled: boolean;
  /** Called with the color the user picks or types. */
  onChange: (value: string) => void;
  /** Keeps the label for screen readers only, for use inside a card whose title says it. */
  labelVisuallyHidden?: boolean;
}

/**
 * Lets the agency pick its brand color from presets, the system color picker,
 * or a HEX value, and shows how a button looks with readable text on it.
 *
 * @param props - Label, value, and change callback.
 * @returns The swatches, HEX field, sample button, and contrast hint.
 */
export const WebsiteBrandColorInput: React.FC<WebsiteBrandColorInputProps> = ({
  label,
  value,
  disabled,
  onChange,
  labelVisuallyHidden = false,
}) => {
  const { t } = useI18n();
  const hintId = useId();
  const isValidColor = isWebsiteHexColor(value);
  const textTone = isValidColor ? resolveWebsiteBrandTextTone(value) : 'light';

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend
        className={
          labelVisuallyHidden
            ? 'sr-only'
            : 'text-sm font-medium text-neutral-900'
        }
      >
        {label}
      </legend>
      <div className="flex flex-wrap items-center gap-2.5">
        {WEBSITE_BRAND_COLOR_SWATCHES.map((swatch) => {
          const isSelected = isSameWebsiteHexColor(swatch.value, value);

          return (
            <button
              key={swatch.value}
              type="button"
              aria-label={t(swatch.labelKey)}
              aria-pressed={isSelected}
              onClick={() => onChange(swatch.value)}
              className={classes(
                'h-9 w-9 rounded-full border-2 border-white transition-shadow',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-60',
                isSelected
                  ? 'shadow-[0_0_0_2px_#09090A]'
                  : 'shadow-[0_0_0_1px_#D1D5DB]'
              )}
              style={{ backgroundColor: swatch.value }}
            />
          );
        })}
        <ColorInput
          label={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          invalid={!isValidColor}
          describedBy={hintId}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {isValidColor ? (
          <span
            aria-hidden="true"
            className="inline-flex h-9 items-center rounded-md px-3 text-sm font-semibold"
            style={{
              backgroundColor: value,
              color:
                textTone === 'light'
                  ? '#FFFFFF'
                  : WEBSITE_BRAND_DARK_TEXT_COLOR,
            }}
          >
            {t('websites.settings.brand.color.sample')}
          </span>
        ) : null}
        <Text
          as="p"
          size="small"
          color={isValidColor && textTone === 'light' ? 'success' : 'neutral'}
          className="flex items-center gap-1.5"
        >
          <span id={hintId} className="inline-flex items-center gap-1.5">
            {isValidColor && textTone === 'light' ? (
              <PiCheckBold aria-hidden="true" className="h-3.5 w-3.5" />
            ) : null}
            {t(resolveColorHintKey(isValidColor, textTone))}
          </span>
        </Text>
      </div>
    </fieldset>
  );
};
